import type { Tag } from '#shared/types'

interface Props {
  /** The active tags, in name order. Archived ones are not offered. */
  tags: Tag[]
  selected: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
}

/**
 * Zero or more tags for one entry.
 *
 * Toggle chips rather than a multi-select: the vocabulary is short, shared by
 * every project, and the answer is usually none or one. A multi-select listbox
 * would hide the options behind a click and make "none" look like a mistake,
 * which it is not — no project requires a tag.
 *
 * Identity never rests on the fill alone: a selected chip carries a check as
 * well, and `aria-pressed` says the same thing to a screen reader.
 */
export function TagPicker({ tags, selected, onChange, disabled = false }: Props) {
  if (tags.length === 0) {
    return (
      <p className="muted hint">
        No tags yet — a manager can add them on the Projects page.
      </p>
    )
  }

  const chosen = new Set(selected)

  function toggle(id: string) {
    onChange(chosen.has(id) ? selected.filter((s) => s !== id) : [...selected, id])
  }

  return (
    <div className="tag-picker" role="group" aria-label="Tags">
      {tags.map((tag) => {
        const on = chosen.has(tag.id)
        return (
          <button
            key={tag.id}
            type="button"
            className={`chip tag-chip${on ? ' chip-on' : ''}`}
            aria-pressed={on}
            disabled={disabled}
            onClick={() => toggle(tag.id)}
          >
            {/* A tick as well as the fill, so the state is not colour-only. */}
            <span className="tag-chip-mark" aria-hidden="true">
              {on ? '✓' : '+'}
            </span>
            {tag.name}
          </button>
        )
      })}
    </div>
  )
}
